import { useEffect, useMemo, useState } from "react"
import classNames from "classnames"

import { ApiTypes } from "types"

import useUrlQueryParams from "hooks/useUrlQueryParams"

import { PaginationDropdown } from "../PaginationDropdown"
import PaginationItems from "./PaginationItems"

import styles from "./index.module.scss"

interface Props {
  meta?: ApiTypes.Model.Pagination
  paddingNone?: boolean
  perPage?: number
  singleLine?: boolean
  queryParamPrefix?: string
  defaultPerPage?: number
  numbersOfPage?: number[]
}

export const Pagination = ({
  meta,
  paddingNone,
  perPage,
  singleLine,
  queryParamPrefix,
  defaultPerPage = 15,
  numbersOfPage = [50, 30, 15, 5],
}: Props) => {
  const { setUrlQueryParams, urlQueryParams } = useUrlQueryParams()
  const pageParam = queryParamPrefix ? `${queryParamPrefix}_page` : "page"
  const perPageParam = queryParamPrefix ? `${queryParamPrefix}_perPage` : "perPage"

  const getPerPage = () => {
    const value = perPage ?? urlQueryParams[perPageParam] ?? defaultPerPage
    return typeof value === "string" ? parseInt(value, 10) : value
  }

  const [data, setData] = useState({
    currentPage: 0,
    from: 0,
    lastPage: 0,
    total: 0,
    perPage: getPerPage(),
  })

  const onPageChange = (value: number) => {
    setUrlQueryParams({ [pageParam]: value })
    setData(prevStats => ({
      ...prevStats,
      page: value,
    }))
  }

  const onPerPageChange = (value: number) => {
    setData(prevStats => ({
      ...prevStats,
      perPage: value,
      currentPage: 1,
    }))

    setUrlQueryParams({
      [perPageParam]: value,
      [pageParam]: 1,
    })
  }

  useEffect(() => {
    if (!meta) return

    const { current_page, from, last_page, per_page, total } = meta

    setData({
      currentPage: current_page,
      lastPage: last_page,
      perPage: perPage ?? per_page,
      total: total,
      from,
    })

    if (current_page > last_page) {
      onPageChange(last_page)
    }
  }, [meta, perPage])

  const pagesCount = useMemo(() => {
    if (data.total === 0) {
      return 1
    }

    return Math.ceil(data.total / data.perPage)
  }, [data])

  const currentPage = data.total === 0 ? 1 : data.currentPage

  return (
    <div
      className={classNames(styles.paginationWrapper, {
        [styles.paddingNone]: paddingNone,
        [styles.singleLine]: singleLine,
      })}
    >
      <div className={styles.paginationText}>
        Page {currentPage} of {pagesCount}
      </div>
      <PaginationItems currentPage={currentPage} totalPages={pagesCount} onPageChange={onPageChange} />
      <PaginationDropdown onSelect={onPerPageChange} currentNumber={data.perPage} numbersOfPage={numbersOfPage} />
    </div>
  )
}
